import { useState, useRef } from 'react';
import { useSnackbar } from 'notistack';
// @mui
import { CircularProgress } from '@mui/material';
// ui
import { Button } from 'src/@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'src/@/components/ui/dialog';
// components
import Iconify from 'src/components/Iconify';
// actions
import { v2_actionImportarProdutos, v2_actionContarProdutos } from 'src/actions/v2/cadastro/v2_actionProdutos';

// ----------------------------------------------------------------------

export default function ModalImportarProdutos({ open, onClose, onSuccess, cliente }) {
    const { enqueueSnackbar } = useSnackbar();
    const [uploading, setUploading] = useState(false);
    const [file, setFile] = useState(null);
    const [result, setResult] = useState(null);
    const [csvRowCount, setCsvRowCount] = useState(0);
    const [limitInfo, setLimitInfo] = useState(null);
    const fileInputRef = useRef(null);

    const handleFileChange = async (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            if (selectedFile.type !== 'text/csv' && !selectedFile.name.endsWith('.csv')) {
                enqueueSnackbar('Por favor, selecione um arquivo CSV.', { variant: 'warning' });
                return;
            }
            setFile(selectedFile);
            setResult(null);

            // Count rows in CSV (excluding header)
            try {
                const text = await selectedFile.text();
                const lines = text.split('\n').filter((line) => line.trim());
                const rowCount = Math.max(0, lines.length - 1); // Exclude header
                setCsvRowCount(rowCount);

                // Fetch current product count
                if (cliente?._id) {
                    const countResponse = await v2_actionContarProdutos(cliente._id);
                    const info = countResponse.data?.data;
                    setLimitInfo(info);

                    if (info && rowCount > info.available) {
                        enqueueSnackbar(
                            `Atenção: O arquivo contém ${rowCount} produto(s), mas você só pode criar mais ${info.available}. Produtos existentes serão atualizados.`,
                            { variant: 'warning' }
                        );
                    }
                }
            } catch (err) {
                console.error('Erro ao processar arquivo:', err);
            }
        }
    };

    const handleUpload = async () => {
        if (!file) return;
        if (!cliente?._id) {
            enqueueSnackbar('Erro: Cliente não identificado.', { variant: 'error' });
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('ownerId', cliente._id);
            const response = await v2_actionImportarProdutos(formData);
            if (response.data && !response.data.error) {
                setResult(response.data.data);
                enqueueSnackbar('Importação concluída!', { variant: 'success' });
                if (onSuccess) onSuccess();
            } else {
                throw new Error(response.data?.message || 'Erro deconhecido');
            }
        } catch (error) {
            console.error(error);
            const errorMsg = error.response?.data?.message || 'Falha na importação.';
            enqueueSnackbar(errorMsg, { variant: 'error' });
        } finally {
            setUploading(false);
        }
    };

    const handleDownloadTemplate = () => {
        const headers = ['sku', 'name', 'price', 'stock', 'ref', 'tags', 'image', 'atrib.tamanho', 'atrib.cor', 'description'];
        const rows = [
            ['PROD-001', 'Camiseta Básica', '49.90', '100', 'REF-001', '"verão, roupas"', 'https://exemplo.com/foto.jpg', 'M', 'Azul', 'Camiseta 100% algodão'],
        ];
        const csvContent = 'data:text/csv;charset=utf-8,' + headers.join(',') + '\n' + rows.map((e) => e.join(',')).join('\n');
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement('a');
        link.setAttribute('href', encodedUri);
        link.setAttribute('download', 'modelo_importacao_produtos.csv');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleClose = () => {
        setFile(null);
        setResult(null);
        setCsvRowCount(0);
        setLimitInfo(null);
        onClose();
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="max-w-md w-full bg-white border-none shadow-2xl rounded-2xl p-0 overflow-hidden">
                <div className="p-6 border-b border-gray-100">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <div className="p-2 bg-green-100 rounded-lg">
                                <Iconify icon="solar:file-download-bold" className="w-5 h-5 text-green-600" />
                            </div>
                            Importar Produtos
                        </DialogTitle>
                    </DialogHeader>
                </div>

                <div className="p-6 flex flex-col items-center gap-6">
                    {!result ? (
                        <>
                            <div
                                className={`w-full h-48 border-2 border-dashed rounded-xl flex flex-col items-center justify-center cursor-pointer transition-colors ${file ? 'border-green-500 bg-green-50' : 'border-gray-300 hover:border-violet-500 hover:bg-violet-50'
                                    }`}
                                onClick={() => !uploading && fileInputRef.current?.click()}
                            >
                                {uploading ? (
                                    <CircularProgress />
                                ) : file ? (
                                    <>
                                        <Iconify icon="solar:file-check-bold" className="w-12 h-12 text-green-500 mb-2" />
                                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {(file.size / 1024).toFixed(1)} KB • {csvRowCount} produto(s)
                                        </p>
                                        {limitInfo && (
                                            <p className="text-xs text-gray-400 mt-1">
                                                Limite: {limitInfo.count}/{limitInfo.limit} ({limitInfo.available} disponível)
                                            </p>
                                        )}
                                    </>
                                ) : (
                                    <>
                                        <Iconify icon="solar:upload-minimalistic-bold" className="w-12 h-12 text-gray-300 mb-2" />
                                        <p className="text-sm font-medium text-gray-600">Clique para selecionar o CSV</p>
                                        <p className="text-xs text-gray-400 mt-1">.csv</p>
                                    </>
                                )}
                                <input type="file" ref={fileInputRef} className="hidden" accept=".csv" onChange={handleFileChange} disabled={uploading} />
                            </div>

                            <div className="w-full bg-gray-50 p-4 rounded-lg text-xs text-gray-500 space-y-2">
                                <div className="flex justify-between items-center">
                                    <p className="font-semibold text-gray-700">Estrutura do Arquivo CSV</p>
                                    <Button
                                        variant="link"
                                        className="h-auto p-0 text-[10px] text-violet-600 hover:text-violet-700"
                                        onClick={handleDownloadTemplate}
                                    >
                                        Baixar Modelo
                                    </Button>
                                </div>
                                <div className="space-y-1">
                                    <p>
                                        • <span className="font-mono text-gray-700">sku</span> (Obrigatório): Identificador único.
                                    </p>
                                    <p>
                                        • <span className="font-mono text-gray-700">name</span>: Nome do produto.
                                    </p>
                                    <p>
                                        • <span className="font-mono text-gray-700">image</span>: URL da imagem do produto.
                                    </p>
                                    <p>
                                        • <span className="font-mono text-gray-700">tags</span>: Separe por vírgulas (ex: verao,oferta).
                                    </p>
                                    <p>
                                        • <span className="font-mono text-gray-700">atrib.Nome</span>: Atributos dinâmicos (ex: atrib.cor, atrib.tamanho).
                                    </p>
                                </div>
                            </div>

                            <div className="flex w-full gap-3 mt-2">
                                <Button variant="outline" onClick={handleClose} className="flex-1 rounded-xl" disabled={uploading}>
                                    Cancelar
                                </Button>
                                <Button
                                    onClick={handleUpload}
                                    className="flex-1 rounded-xl bg-[#7C3AED] hover:bg-[#6d28d9] text-white"
                                    disabled={!file || uploading}
                                >
                                    {uploading ? 'Importando...' : 'Importar CSV'}
                                </Button>
                            </div>
                        </>
                    ) : (
                        <div className="w-full flex flex-col items-center animate-in fade-in zoom-in duration-300">
                            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-4 text-green-600">
                                <Iconify icon="solar:check-circle-bold" className="w-8 h-8" />
                            </div>
                            <h3 className="text-lg font-bold text-gray-900 mb-1">Importação Finalizada</h3>
                            <div className="grid grid-cols-3 gap-3 w-full my-6">
                                <div className="p-3 bg-blue-50 rounded-xl text-center">
                                    <p className="text-2xl font-bold text-blue-600">{result.created}</p>
                                    <p className="text-xs text-blue-800 font-medium">Novos</p>
                                </div>
                                <div className="p-3 bg-yellow-50 rounded-xl text-center">
                                    <p className="text-2xl font-bold text-yellow-600">{result.updated}</p>
                                    <p className="text-xs text-yellow-800 font-medium">Atualizados</p>
                                </div>
                                <div className="p-3 bg-red-50 rounded-xl text-center">
                                    <p className="text-2xl font-bold text-red-600">{result.errors}</p>
                                    <p className="text-xs text-red-800 font-medium">Erros</p>
                                </div>
                            </div>
                            <Button onClick={handleClose} className="w-full rounded-xl bg-gray-900 text-white hover:bg-gray-800">
                                Concluir
                            </Button>
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
}